import { useMemo, useState } from "react";
import { Link } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { TextInput } from "@/components/design/TextField";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { LoadMore } from "@/components/common/LoadMore";
import { InviteExistingPersonDialog } from "@/components/invite/InviteExistingPersonDialog";
import { useGlobalSearch } from "@/lib/use-global-search";
import {
  PersonLoginAction,
  PersonSearchScope,
  SearchEntityType,
  PersonLoginStatus,
  type PersonLoginAssignmentFieldsFragment,
} from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

// @external-adr GENESIS-0046-a-null-subject-means-no-account-binding
const PAGE_SIZE = 25;

export function LoginAssignmentPanel() {
  const intl = useIntl();
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(PersonLoginStatus.Unassigned);
  const [dialog, setDialog] = useState<PersonLoginAssignmentFieldsFragment | null>(null);

  const input = useMemo(() => ({
    query: search || null,
    entityTypes: [SearchEntityType.Person],
    personSearchScope: PersonSearchScope.Identity,
    personLoginStatus: [status],
  }), [search, status]);
  const paged = useGlobalSearch({ input, pageSize: PAGE_SIZE, includeLoginAssignment: true });
  const rows = paged.results.flatMap((result) => result.loginAssignment ? [result.loginAssignment] : []);
  const { loading, error, refetch } = paged;
  const otherStatus = status === PersonLoginStatus.Assigned
    ? PersonLoginStatus.Unassigned : PersonLoginStatus.Assigned;
  const checkOtherStatus = Boolean(search) && !loading && !error && rows.length === 0;
  const otherInput = useMemo(() => ({ ...input, personLoginStatus: [otherStatus] }), [input, otherStatus]);
  const otherSearch = useGlobalSearch({ input: otherInput, pageSize: 1, skip: !checkOtherStatus });
  const hasOtherResults = checkOtherStatus && !otherSearch.loading && !otherSearch.error
    && otherSearch.results.length > 0;

  return (
    <div className="bg-surface box p-5">
      <BoxHeader title={intl.formatMessage({ id: "loginAssignment.title", defaultMessage: "Login assignment" })} />

      <p className="mt-2 max-w-2xl text-smycles-meta leading-relaxed text-ink">
        <FormattedMessage
          id="loginAssignment.intro"
          defaultMessage="Assign logins to existing profiles and see whether a sign-in has been recorded."
        />
      </p>

      <SegmentedControl
        id="login-assignment-status"
        className="mt-4"
        aria-label={intl.formatMessage({ id: "loginAssignment.view", defaultMessage: "Login assignment view" })}
        value={status}
        onChange={setStatus}
        options={[
          { value: PersonLoginStatus.Unassigned, label: intl.formatMessage({ id: "loginAssignment.withoutLogin", defaultMessage: "Without login" }) },
          { value: PersonLoginStatus.Assigned, label: intl.formatMessage({ id: "loginAssignment.withLogin", defaultMessage: "With login" }) },
        ]}
      />

      {status === PersonLoginStatus.Assigned ? (
        <p className="mt-3 text-smycles-meta text-ink-muted">
          <FormattedMessage id="loginAssignment.trackingHint" defaultMessage="Sign-ins are recorded since this feature was introduced. Earlier sign-ins are not included." />
        </p>
      ) : null}

      <form
        className="mt-5 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(searchDraft.trim());
        }}
      >
        <label className="sr-only" htmlFor="login-assignment-search">
          <FormattedMessage id="loginAssignment.searchLabel" defaultMessage="Search by name or address" />
        </label>
        <TextInput
          id="login-assignment-search"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder={intl.formatMessage({
            id: "loginAssignment.searchPlaceholder",
            defaultMessage: "Name or address",
          })}
          className="max-w-xs"
        />
        <Button id="login-assignment-search-submit" size="compact" variant="secondary" type="submit">
          <FormattedMessage id="common.search" defaultMessage="Search" />
        </Button>
      </form>

      {error && !rows.length ? (
        <p role="alert" className="mt-4 text-smycles-meta text-ink">{userErrorMessage(intl, error)}</p>
      ) : null}

      <ul
        id="login-assignment-rows"
        aria-label={intl.formatMessage({ id: "loginAssignment.title", defaultMessage: "Login assignment" })}
        className="-mx-2 mt-4 divide-y divide-ink/10"
      >
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex flex-wrap items-center gap-x-4 gap-y-1 px-2 py-3 transition-colors hover:bg-ink/[0.06]"
          >
            <div className="min-w-0 basis-full @sm:flex-1 @sm:basis-auto">
              {/* Only the name is the link, not the row: the row carries a button,
                  and an anchor wrapping a button is the nesting that makes a click
                  ambiguous. */}
              <Link
                id={`login-assignment-profile-${row.personId}`}
                to={`/p/${row.personId}`}
                title={intl.formatMessage(
                  { id: "loginAssignment.openProfile", defaultMessage: "Open profile of {name}" },
                  { name: row.name },
                )}
                className="block truncate rounded text-smycles-body text-ink transition hover:text-ink-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent/50"
              >
                {row.name}
              </Link>
              <p className="truncate text-smycles-meta text-ink-muted">
                {row.suggestedEmail ?? intl.formatMessage({ id: "loginAssignment.noEmail", defaultMessage: "No email on file" })}
              </p>
            </div>
            {row.status === PersonLoginStatus.Assigned ? (
              <p className="basis-full text-smycles-meta text-ink-muted @sm:basis-auto">
                {row.firstAuthenticatedAt ? (
                  <FormattedMessage
                    id="loginAssignment.firstAuthenticatedAt"
                    defaultMessage="First recorded sign-in on {date} at {time}"
                    values={{
                      date: intl.formatDate(row.firstAuthenticatedAt, { year: "numeric", month: "2-digit", day: "2-digit" }),
                      time: intl.formatTime(row.firstAuthenticatedAt, { hour: "2-digit", minute: "2-digit" }),
                    }}
                  />
                ) : (
                  <FormattedMessage id="loginAssignment.noAuthentication" defaultMessage="No sign-in recorded yet" />
                )}
              </p>
            ) : (
              <div className="flex basis-full justify-start @sm:w-44 @sm:shrink-0 @sm:basis-auto @sm:justify-end">
                <RowAction row={row} onOpen={() => setDialog(row)} />
              </div>
            )}
          </li>
        ))}
      </ul>

      {loading && !rows.length ? (
        <p role="status" className="py-3 text-smycles-meta text-ink-muted">
          <FormattedMessage id="loginAssignment.loading" defaultMessage="Loading…" />
        </p>
      ) : null}
      {!loading && !error && rows.length === 0 ? (
        <p className="py-3 text-smycles-meta text-ink-muted">
          {status === PersonLoginStatus.Assigned
            ? (search
              ? <FormattedMessage id="loginAssignment.emptyAssignedSearch" defaultMessage="No person with a login matches the search." />
              : <FormattedMessage id="loginAssignment.emptyAssigned" defaultMessage="No logins have been assigned yet." />)
            : search
            ? <FormattedMessage id="loginAssignment.emptySearch" defaultMessage="No person without a login matches the search." />
            : <FormattedMessage id="loginAssignment.empty" defaultMessage="Everybody has a login." />}
        </p>
      ) : null}

      {hasOtherResults ? (
        <Button id="login-assignment-other-results" size="compact" variant="secondary" onClick={() => setStatus(otherStatus)}>
          {otherStatus === PersonLoginStatus.Assigned
            ? <FormattedMessage id="loginAssignment.showAssignedResults" defaultMessage="Show matches under “With login”" />
            : <FormattedMessage id="loginAssignment.showUnassignedResults" defaultMessage="Show matches under “Without login”" />}
        </Button>
      ) : null}

      <LoadMore
        hasMore={paged.hasMore}
        loading={paged.loadingMore}
        paused={paged.loadMoreFailed}
        onLoadMore={paged.loadMore}
      />

      {dialog ? (
        <InviteExistingPersonDialog
          key={dialog.personId}
          assignment={dialog}
          onClose={() => setDialog(null)}
          onCompleted={() => void refetch().catch(() => undefined)}
        />
      ) : null}
    </div>
  );
}

function RowAction({ row, onOpen }: { row: PersonLoginAssignmentFieldsFragment; onOpen: () => void }) {
  if (row.actions.includes(PersonLoginAction.Invite)) {
    return (
      <Button id={`login-assignment-invite-${row.personId}`} size="compact" variant="secondary" onClick={onOpen}>
        <FormattedMessage id="loginAssignment.action.invite" defaultMessage="Invite" />
      </Button>
    );
  }
  // genesis offers nothing while an earlier attempt is still unresolved: a second
  // invitation is exactly what could create the second account.
  return (
    <span className="text-smycles-meta text-ink-muted">
      <FormattedMessage id="loginAssignment.action.pending" defaultMessage="Earlier invitation unresolved" />
    </span>
  );
}
